import { useEffect, useState } from "react";
import { Link, NavLink, useLocation } from "react-router-dom";
import { motion, AnimatePresence } from "framer-motion";
import axios from "axios";
import { Menu, X, LayoutDashboard, Flame, GraduationCap, ChevronDown, LogIn, UserPlus } from "lucide-react";
import { NAV } from "@/constants/testIds";
import { useAuth } from "@/context/AuthContext";
import { UserAvatar } from "@/components/UserAvatar";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const INDUSTRY_LINKS = [
  { to: "/for/healthcare", label: "Healthcare" },
  { to: "/for/first-responders", label: "First Responders" },
  { to: "/for/hospitality", label: "Hospitality" },
  { to: "/for/call-centers", label: "Call Centers" },
];

const links = [
  { to: "/", label: "Home", id: NAV.home },
  { to: "/about", label: "What We Do", id: "nav-about" },
  { to: "/manifesto", label: "Manifesto", id: NAV.manifesto },
  { to: "/modules", label: "Modules", id: NAV.modules },
  { to: "/peer-plus", label: "Luma", id: "nav-luma" },
  { to: "/books", label: "Books", id: NAV.books },
  { to: "/shop", label: "Shop", id: NAV.shop, accent: true },
  { to: "/b2b", label: "For Companies", id: NAV.b2b },
  { to: "https://tacc-praxis.polsia.app", label: "Story Room", id: "nav-story-room", external: true },
];

export default function Navigation() {
  const [scrolled, setScrolled] = useState(false);
  const [open, setOpen] = useState(false);
  const [prog, setProg] = useState(null);
  const location = useLocation();
  const { user } = useAuth();

  useEffect(() => {
    if (!user) { setProg(null); return; }
    axios.get(`${API}/progress/me`).then(({ data }) => setProg(data)).catch(() => {});
  }, [user, location.pathname]);

  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 30);
    onScroll();
    window.addEventListener("scroll", onScroll);
    return () => window.removeEventListener("scroll", onScroll);
  }, []);

  useEffect(() => { setOpen(false); }, [location.pathname]);

  const renderDesktopLink = (l) => {
    if (l.to === "/b2b") {
      return (
        <div key={l.to} className="relative group">
          <NavLink
            to={l.to}
            data-testid={l.id}
            className={({ isActive }) => `font-mono text-[11px] tracking-[0.3em] uppercase whitespace-nowrap tacc-link inline-flex items-center gap-1.5 ${isActive ? "text-[#B45309]" : ""}`}
          >
            {l.label}
            <ChevronDown size={11} className="opacity-60 group-hover:rotate-180 transition-transform duration-300" />
          </NavLink>
          <div className="absolute left-1/2 -translate-x-1/2 top-full pt-5 opacity-0 pointer-events-none group-hover:opacity-100 group-hover:pointer-events-auto transition-opacity duration-200" data-testid="nav-industries-dropdown">
            <div className="bg-white backdrop-blur-2xl border border-[#E7DFD5] shadow-[0_16px_40px_-12px_rgba(44,39,32,0.15)] rounded-xl py-3 min-w-[220px] flex flex-col">
              {INDUSTRY_LINKS.map((s) => (
                <NavLink
                  key={s.to}
                  to={s.to}
                  data-testid={`nav-industry-${s.to.split("/").pop()}`}
                  className={({ isActive }) => `px-5 py-2.5 font-mono text-[11px] tracking-[0.25em] uppercase hover:text-[#B45309] hover:bg-black/5 transition-colors ${isActive ? "text-[#B45309]" : "text-[#44403C]"}`}
                >
                  {s.label}
                </NavLink>
              ))}
            </div>
          </div>
        </div>
      );
    }
    if (l.external) {
      return (
        <a
          key={l.to}
          href={l.to}
          target="_blank"
          rel="noreferrer"
          data-testid={l.id}
          className="font-mono text-[11px] tracking-[0.3em] uppercase whitespace-nowrap tacc-link"
        >
          {l.label}
        </a>
      );
    }
    return (
      <NavLink
        key={l.to}
        to={l.to}
        data-testid={l.id}
        className={({ isActive }) => `font-mono text-[11px] tracking-[0.3em] uppercase whitespace-nowrap tacc-link ${isActive ? "text-[#B45309]" : ""} ${l.accent ? "!text-[#B45309]" : ""}`}
      >
        {l.label}
      </NavLink>
    );
  };

  return (
    <motion.header
      initial={{ y: -60, opacity: 0 }}
      animate={{ y: 0, opacity: 1 }}
      transition={{ duration: 0.9, ease: [0.65, 0, 0.35, 1], delay: 0.2 }}
      className={`fixed top-0 left-0 right-0 z-50 ${scrolled ? "backdrop-blur-2xl bg-[#FAF7F2]/85 border-b border-[#E7DFD5] shadow-[0_4px_20px_-4px_rgba(44,39,32,0.06)]" : "bg-transparent"} transition-[background-color,border-color] duration-500`}
    >
      <div className="max-w-[1440px] mx-auto px-6 md:px-10 py-5 flex items-center justify-between">
        <Link to="/" data-testid={NAV.logo} className="flex items-center gap-3 group">
          <div className="w-8 h-8 border border-[#F59E0B]/60 flex items-center justify-center">
            <span className="font-display text-[#B45309] text-lg leading-none">T</span>
          </div>
          <div className="hidden sm:flex flex-col leading-none">
            <span className="font-mono text-[10px] tracking-[0.35em] text-[#78716C]">EST. 2025</span>
            <span className="font-heading text-sm tracking-[0.2em] mt-0.5">T.A.C.C.</span>
          </div>
        </Link>

        <nav className="hidden lg:flex items-center gap-7">
          {links.map(renderDesktopLink)}
        </nav>

        <div className="flex items-center gap-3">
          {user && prog && (
            <div className="hidden lg:flex items-center gap-4 ml-5 mr-3" data-testid="nav-member-status">
              <Link
                to="/dashboard"
                data-testid="nav-streak"
                title={`${prog.streak_current}-day resilience streak`}
                className="flex items-center gap-1.5 font-mono text-[11px] text-[#B45309] hover:opacity-80 transition-opacity"
              >
                <Flame size={13} strokeWidth={1.6} />
                {prog.streak_current}
              </Link>
              <Link
                to="/dashboard"
                data-testid="nav-progress"
                title="Modules complete"
                className="font-mono text-[11px] text-[#78716C] hover:text-[#B45309] transition-colors"
              >
                {prog.completed_count}/{prog.total_modules}
              </Link>
              {prog.graduated && (
                <Link
                  to="/modules/graduation/watch"
                  data-testid="nav-graduation-link"
                  title="Watch your graduation video"
                  className="text-[#B45309] hover:opacity-80 transition-opacity"
                >
                  <GraduationCap size={16} strokeWidth={1.6} />
                </Link>
              )}
            </div>
          )}
          {user && user.role === "admin" && (
            <Link to="/admin" data-testid="nav-admin" className="hidden md:inline-flex font-mono text-[11px] tracking-[0.3em] uppercase whitespace-nowrap tacc-link ml-6 mr-2">
              Admin
            </Link>
          )}
          {user ? (
            <>
              <Link
                to="/dashboard"
                aria-label="Member Dashboard"
                data-testid="nav-avatar-link"
                className="hidden md:inline-flex ml-3"
              >
                <UserAvatar picture={user.picture} name={user.name} size={34} testId="nav-avatar" />
              </Link>
              <Link
                to="/dashboard"
                data-testid={NAV.dashboard}
                className="hidden md:inline-flex btn-outline text-sm whitespace-nowrap ml-1"
              >
                <LayoutDashboard size={15} />
                Member Dashboard
              </Link>
              <Link
                to="/b2b#quote"
                data-testid={NAV.quoteCta}
                className="hidden md:inline-flex btn-ember text-sm whitespace-nowrap"
              >
                Request a Quote
              </Link>
              <Link
                to="/dashboard"
                aria-label="Member Dashboard"
                data-testid="nav-mobile-dashboard-btn"
                className="md:hidden"
              >
                <UserAvatar picture={user.picture} name={user.name} size={40} testId="nav-mobile-avatar" />
              </Link>
            </>
          ) : (
            <>
              <Link
                to="/login"
                data-testid="nav-login-btn"
                className="inline-flex btn-ember text-xs md:text-sm !px-4 !py-2.5 md:!px-6 md:!py-3.5 whitespace-nowrap ml-3"
              >
                <LogIn size={15} />
                Login
              </Link>
              <Link
                to="/register"
                data-testid="nav-signup-btn"
                className="hidden md:inline-flex btn-ember text-sm whitespace-nowrap"
              >
                <UserPlus size={15} />
                Sign Up
              </Link>
            </>
          )}
          <button
            aria-label="Menu"
            onClick={() => setOpen((o) => !o)}
            className="lg:hidden w-10 h-10 border border-[#E7DFD5] flex items-center justify-center"
            data-testid="nav-mobile-toggle"
          >
            {open ? <X size={16} /> : <Menu size={16} />}
          </button>
        </div>
      </div>

      <AnimatePresence>
        {open && (
          <motion.div
            initial={{ height: 0, opacity: 0 }}
            animate={{ height: "auto", opacity: 1 }}
            exit={{ height: 0, opacity: 0 }}
            transition={{ duration: 0.4, ease: [0.65, 0, 0.35, 1] }}
            className="lg:hidden overflow-hidden border-t border-[#E7DFD5] bg-[#FAF7F2]"
          >
            <div className="px-6 py-6 flex flex-col gap-4 max-h-[calc(100dvh-84px)] overflow-y-auto pb-10" data-testid="nav-mobile-menu">
              {links.map((l) =>
                l.external ? (
                  <a key={l.to} href={l.to} target="_blank" rel="noreferrer" className="font-display text-2xl tracking-tight">
                    {l.label}
                  </a>
                ) : (
                  <NavLink
                    key={l.to}
                    to={l.to}
                    className="font-display text-2xl tracking-tight"
                  >
                    {l.label}
                  </NavLink>
                )
              )}
              <div className="flex flex-col gap-2.5 pl-4 border-l border-[#F59E0B]/30">
                {INDUSTRY_LINKS.map((s) => (
                  <NavLink key={s.to} to={s.to} className="font-mono text-[11px] tracking-[0.25em] uppercase text-[#44403C]">
                    For {s.label}
                  </NavLink>
                ))}
              </div>
              {user ? (
                <div className="flex flex-wrap gap-3 mt-2">
                  <Link to="/dashboard" className="btn-outline w-fit">Member Dashboard</Link>
                  <Link to="/b2b#quote" className="btn-ember w-fit">Request a Quote</Link>
                  {user.role === "admin" && <Link to="/admin" data-testid="nav-mobile-admin" className="btn-outline w-fit">Admin</Link>}
                </div>
              ) : (
                <div className="flex flex-wrap gap-3 mt-2">
                  <Link to="/login" data-testid="nav-mobile-login-btn" className="btn-ember w-fit">
                    <LogIn size={15} /> Login
                  </Link>
                  <Link to="/register" data-testid="nav-mobile-signup-btn" className="btn-ember w-fit">
                    <UserPlus size={15} /> Sign Up
                  </Link>
                  <Link to="/b2b#quote" className="btn-outline w-fit">Request a Quote</Link>
                </div>
              )}
            </div>
          </motion.div>
        )}
      </AnimatePresence>
    </motion.header>
  );
}
